import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, Optional, inject, input } from '@angular/core';
import { BasketTotalsComponentDef, IBasketTotalsItem } from '@df/basket/view/basket-totals.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { TimeoutService } from '@df/ng/timeout.service';
import { Customer } from '@df/session/api/customer';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';

import { CheckoutFormComponent } from '../../checkout/form/checkout-form.component';
import { BasketDeliveryMethodsComponent } from '../delivery-methods/basket-delivery-methods.component';
import { AbService } from '@df/module-ab/ab.service';
import { NBasketDeliveryMethods } from '../delivery-methods/basket-delivery-methods.namespace';
import { TaxMode } from '@df/ui/price/price.service';

declare module '@df/session/api/basket/basket' {
    interface IBasketTotals {
        discount_success_message?: string[];
        discounts_applied?: number;
    }
}

@Component({
    selector: 'basket-view-totals',
    templateUrl: './basket-view-totals.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, PriceComponent, TranslationModule, BasketDeliveryMethodsComponent]
})
export class BasketViewTotalsComponent extends BasketTotalsComponentDef implements OnInit, OnDestroy {
    context = input<'default' | 'basket'>('default');

    protected displayBasketDeliveryMethods = false;

    private selectedDeliveryMethod?: Checkout.IShippingMethod;

    private abService = inject(AbService);

    constructor(
        protected override changeDetectorRef: ChangeDetectorRef,
        protected override app: App,
        protected override customer: Customer,
        protected override timeoutService: TimeoutService,
        @Optional() protected checkoutFormComponent?: CheckoutFormComponent
    ) {
        super(changeDetectorRef, app, customer, timeoutService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.TotalsUpdate).subscribe(() => {
                this.update();
            })
        );
        if (this.isBasketView) {
            const experimentSubject = this.abService.getSubject(NBasketDeliveryMethods.EXPERIMENT_CODE);

            this.subscriptions.add(
                experimentSubject.subscribe(state => {
                    if (state.variant === 'B') {
                        this.displayBasketDeliveryMethods = true;
                        this.alwaysDisplaySubtotal = true;
                        this.update();
                    }
                })
            );
        }
    }

    private get isBasketView(): boolean {
        return this.context() === 'basket';
    }

    protected onDeliveryMethodSelected(selection: Checkout.IShippingMethod): void {
        this.selectedDeliveryMethod = selection;
        this.update();
    }

    override get basket() {
        return (<CheckoutFormComponent>this.checkoutFormComponent)?.isSamples ? this.customer.samplesBasket : this.customer.basket;
    }

    protected override getTotalValue(): number {
        const totalValue = super.getTotalValue();
        if (this.displayBasketDeliveryMethods) {
            const basketShippingValue = this.basket.getShipping(TaxMode.including) || 0;
            return (totalValue * 100 - basketShippingValue * 100 + (this.selectedDeliveryMethod?.price || 0) * 100) / 100;
        }
        return totalValue;
    }

    protected override get withShipping(): boolean {
        return this.displayBasketDeliveryMethods ? false : super.withShipping;
    }

    protected override getTotalLabel(): string {
        return this.displayBasketDeliveryMethods ? 'Order total' : super.getTotalLabel();
    }

    itemLabel(item: IBasketTotalsItem): string {
        const successMessage = this.basket.totals?.discount_success_message?.[0];
        return item.id === 'discount'
            ? (this.basket.totals?.discounts_applied || 0) > 1
                ? 'Multiple discounts'
                : successMessage
                  ? successMessage
                  : item.label
            : item.label;
    }
}
